import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import {
  Clock, Upload, X, ChevronDown, ChevronUp, AlertTriangle, CheckCircle,
  FileImage, Download, Trash2, Plus, Calendar, Phone, Building2,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const BASE_URL = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(); }
  catch { return iso; }
};

const daysUntil = (iso) => {
  if (!iso) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const target = new Date(iso); target.setHours(0, 0, 0, 0);
  return Math.round((target - today) / 86400000);
};

/* ------------------------------- Create Modal ------------------------------ */

const HoldCallModal = ({ onClose, onSaved }) => {
  const [form, setForm] = useState({
    order_number: "", order_type: "External", notes: "",
    denied_reason: "", agency_contact_at: "",
  });
  const [proof1, setProof1] = useState(null);
  const [proof2, setProof2] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const update = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.order_number.trim()) { setError("Order Number is required"); return; }
    if (!form.denied_reason.trim()) { setError("Denied Reason is required"); return; }
    setSaving(true); setError(null);
    try {
      const fd = new FormData();
      fd.append("order_number", form.order_number.trim());
      fd.append("order_type", form.order_type);
      fd.append("notes", form.notes);
      fd.append("denied_reason", form.denied_reason);
      fd.append("agency_contact_at", form.agency_contact_at);
      if (proof1) fd.append("proof1", proof1);
      if (proof2) fd.append("proof2", proof2);
      await axios.post(`${API}/hold-calls`, fd);
      onSaved(); onClose();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl border border-gray-200 p-5 w-full max-w-xl shadow-2xl max-h-[92vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
        data-testid="hold-call-modal"
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
              <Clock className="h-5 w-5 text-[#B0803E]" />
              New Hold Call
            </h3>
            <p className="text-[10px] text-gray-500 mt-0.5 uppercase tracking-wider">Release date auto-calculates to today + 3 days</p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100 text-gray-500"><X className="h-4 w-4" /></button>
        </div>
        {error && (
          <div className="mb-3 px-3 py-2 rounded bg-red-50 border border-red-200 text-red-700 text-xs" data-testid="hold-modal-error">{error}</div>
        )}

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Order Number *</label>
              <input
                value={form.order_number} onChange={e => update("order_number", e.target.value)}
                className="w-full bg-white border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#B0803E]"
                placeholder="e.g., 353600300"
                data-testid="hold-order-number"
              />
            </div>
            <div>
              <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Order Type *</label>
              <select
                value={form.order_type} onChange={e => update("order_type", e.target.value)}
                className="w-full bg-white border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#B0803E]"
                data-testid="hold-order-type"
              >
                <option value="Internal">Internal</option>
                <option value="External">External</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Client Denied Reason *</label>
            <input
              value={form.denied_reason} onChange={e => update("denied_reason", e.target.value)}
              className="w-full bg-white border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#B0803E]"
              placeholder="Why did the client deny the inspection?"
              data-testid="hold-denied-reason"
            />
          </div>

          <div>
            <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Notes / Explanation</label>
            <textarea
              value={form.notes} onChange={e => update("notes", e.target.value)}
              className="w-full bg-white border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#B0803E]"
              rows="3"
              placeholder="Call summary, agency response, etc."
              data-testid="hold-notes"
            />
          </div>

          <div>
            <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Agency Contacted At</label>
            <input
              type="datetime-local"
              value={form.agency_contact_at} onChange={e => update("agency_contact_at", e.target.value)}
              className="w-full bg-white border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#B0803E]"
              data-testid="hold-agency-contact-at"
            />
          </div>

          <div>
            <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Proof Files (up to 2)</label>
            <div className="grid grid-cols-2 gap-2">
              <ProofSlot label="Proof 1" file={proof1} onChange={setProof1} testId="hold-proof-1" />
              <ProofSlot label="Proof 2" file={proof2} onChange={setProof2} testId="hold-proof-2" />
            </div>
          </div>

          <div className="bg-[#B0803E]/10 border border-[#B0803E]/30 rounded-lg px-3 py-2 flex items-center gap-2" data-testid="hold-schedule-notice">
            <Calendar className="h-4 w-4 text-[#B0803E] shrink-0" />
            <p className="text-xs text-[#6F4E1D]">
              After you save, this record enters <span className="font-bold">Hold Calls</span> and appears in <span className="font-bold">Hold Review</span> in 3 days.
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold rounded" data-testid="hold-cancel">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-[#B0803E] hover:bg-[#946A32] text-white text-xs font-bold rounded disabled:opacity-50"
            data-testid="hold-save"
          >
            {saving ? "Saving..." : "Save Hold Call"}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ---------------------------- Proof upload slot ---------------------------- */

const ProofSlot = ({ label, file, onChange, testId }) => {
  const ref = useRef(null);
  return (
    <div>
      <p className="text-[10px] text-gray-400 uppercase mb-1">{label}</p>
      <input
        ref={ref}
        type="file"
        accept="image/*,application/pdf"
        className="hidden"
        onChange={(e) => onChange(e.target.files?.[0] || null)}
        data-testid={`${testId}-input`}
      />
      {file ? (
        <div className="flex items-center gap-2 px-2 py-1.5 bg-[#A7C4A0]/15 border border-[#A7C4A0]/40 rounded text-[11px]">
          <FileImage className="h-3.5 w-3.5 text-[#6B8F62]" />
          <span className="flex-1 truncate text-[#4A6B42] font-semibold">{file.name}</span>
          <button onClick={() => onChange(null)} className="text-[#B04840] hover:text-red-700" data-testid={`${testId}-clear`}><X className="h-3.5 w-3.5" /></button>
        </div>
      ) : (
        <button
          onClick={() => ref.current?.click()}
          className="w-full px-2 py-2 border-2 border-dashed border-gray-300 rounded text-[11px] text-gray-500 hover:border-[#B0803E] hover:text-[#B0803E] flex items-center justify-center gap-1"
          data-testid={testId}
        >
          <Upload className="h-3.5 w-3.5" /> Upload
        </button>
      )}
    </div>
  );
};

/* ----------------------------- Hold Call card ------------------------------ */

const HoldCallCard = ({ record, kind, onChanged, isAdmin }) => {
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const days = daysUntil(record.release_date);

  const close = async () => {
    const reason = window.prompt("Closure reason?", "Agency confirmed deny");
    if (!reason) return;
    setBusy(true);
    try {
      await axios.post(`${API}/hold-calls/${record.id}/close`, { closure_reason: reason });
      onChanged();
    } catch (e) {
      window.alert(e.response?.data?.detail || "Failed to close");
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm("Delete this hold call record permanently?")) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/hold-calls/${record.id}`);
      onChanged();
    } catch (e) {
      window.alert(e.response?.data?.detail || "Failed to delete");
    } finally { setBusy(false); }
  };

  const isReview = kind === "review";

  return (
    <div
      className={`rounded-lg border p-3 text-xs transition-colors ${
        isReview
          ? "bg-red-50 border-red-300 hover:border-red-400"
          : "bg-[#B0803E]/10 border-[#B0803E]/30 hover:border-[#B0803E]/50"
      }`}
      data-testid={`hold-card-${record.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-bold text-[13px] text-[#1F2937]">#{record.order_number}</span>
            <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider ${
              record.order_type === "Internal"
                ? "bg-[#2F5D8A]/10 text-[#2F5D8A]"
                : "bg-[#8A5D2F]/10 text-[#8A5D2F]"
            }`}>{record.order_type}</span>
            {isReview ? (
              <span className="px-1.5 py-0.5 rounded bg-red-600 text-white text-[9px] font-bold uppercase tracking-wider animate-pulse">Ready to Review</span>
            ) : (
              <span className="px-1.5 py-0.5 rounded bg-[#B0803E]/20 text-[#6F4E1D] text-[9px] font-bold uppercase tracking-wider">
                Release {days ?? "?"}d
              </span>
            )}
          </div>
          <p className="text-[11px] text-gray-600 truncate" title={record.order_snapshot?.ph_name || ""}>
            {record.order_snapshot?.ph_name || "—"}
          </p>
          <p className="text-[10px] text-gray-500 truncate">
            {[record.order_snapshot?.address, record.order_snapshot?.city, record.order_snapshot?.state].filter(Boolean).join(", ") || "—"}
          </p>
        </div>
        <button
          onClick={() => setExpanded(v => !v)}
          className="p-1 text-gray-500 hover:text-gray-800"
          data-testid={`hold-card-expand-${record.id}`}
        >
          {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
      </div>

      {expanded && (
        <div className="mt-2 pt-2 border-t border-gray-200 space-y-1.5 text-[11px]">
          <div><span className="font-bold text-gray-500 uppercase tracking-wider text-[9px] mr-1">Denied:</span>{record.denied_reason}</div>
          {record.notes && <div><span className="font-bold text-gray-500 uppercase tracking-wider text-[9px] mr-1">Notes:</span>{record.notes}</div>}
          <div className="flex gap-3 text-gray-600">
            <span><Calendar className="h-3 w-3 inline mr-1" />Start {fmtDate(record.hold_start_date)}</span>
            <span><Calendar className="h-3 w-3 inline mr-1" />Release {fmtDate(record.release_date)}</span>
          </div>
          {record.agency_contact_at && (
            <div className="text-gray-600"><Phone className="h-3 w-3 inline mr-1" />Agency contacted {fmtDate(record.agency_contact_at)}</div>
          )}
          <div className="text-gray-500 text-[10px]">By {record.created_by}</div>
          {record.proofs && record.proofs.length > 0 && (
            <div className="flex flex-wrap gap-1.5 pt-1">
              {record.proofs.map((p, i) => (
                <a
                  key={i}
                  href={`${BASE_URL}${p.url}`}
                  target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1 px-2 py-0.5 bg-white border border-gray-300 rounded text-[10px] text-[#2F5D8A] hover:bg-[#2F5D8A]/5"
                  data-testid={`hold-proof-link-${record.id}-${i}`}
                >
                  <Download className="h-3 w-3" /> {p.slot}
                </a>
              ))}
            </div>
          )}
          <div className="flex gap-1.5 pt-1">
            {!record.closed_at && (
              <button
                onClick={close}
                disabled={busy}
                className="px-2.5 py-1 bg-[#6B8F62] hover:bg-[#577550] text-white text-[10px] font-bold rounded disabled:opacity-50"
                data-testid={`hold-close-${record.id}`}
              >
                <CheckCircle className="h-3 w-3 inline mr-1" />
                Close (Deny Resolved)
              </button>
            )}
            {isAdmin && (
              <button
                onClick={remove}
                disabled={busy}
                className="px-2.5 py-1 bg-red-100 hover:bg-red-200 text-red-700 text-[10px] font-bold rounded disabled:opacity-50"
                data-testid={`hold-delete-${record.id}`}
              >
                <Trash2 className="h-3 w-3 inline mr-1" /> Delete
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/* ------------------------------- Main section ----------------------------- */

export default function HoldCallsSection() {
  const [holding, setHolding] = useState([]);
  const [review, setReview] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openHoldingAccordion, setOpenHoldingAccordion] = useState(false);
  const [openReviewAccordion, setOpenReviewAccordion] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [role, setRole] = useState("");

  const fetchAll = useCallback(async () => {
    try {
      const [h, r] = await Promise.all([
        axios.get(`${API}/hold-calls`, { params: { status: "holding" } }),
        axios.get(`${API}/hold-calls`, { params: { status: "review" } }),
      ]);
      setHolding(h.data || []);
      setReview(r.data || []);
    } catch (e) {
      console.error("Failed to fetch hold calls", e);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchAll();
    axios.get(`${API}/auth/me`).then(res => setRole(res.data?.role || "")).catch(() => {});
  }, [fetchAll]);

  const isAdmin = role === "Admin";

  return (
    <div data-testid="hold-calls-section">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Hold Calls</h2>
        <button
          onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-1 px-3 py-1 bg-[#B0803E] hover:bg-[#946A32] text-white text-[11px] font-bold rounded"
          data-testid="hold-calls-new-btn"
        >
          <Plus className="h-3 w-3" /> New Hold Call
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* Review accordion — ready for closure */}
        <div
          className={`rounded-lg border-2 ${review.length > 0 ? "border-red-400 bg-red-50/60" : "border-gray-200 bg-white"}`}
          data-testid="hold-review-folder"
        >
          <button
            onClick={() => setOpenReviewAccordion(v => !v)}
            className="w-full px-3 py-2.5 flex items-center justify-between hover:bg-red-50/70 transition-colors"
          >
            <div className="flex items-center gap-2">
              <AlertTriangle className={`h-4 w-4 ${review.length > 0 ? "text-red-600" : "text-gray-400"}`} />
              <span className="text-sm font-bold text-[#1F2937]">Hold Review</span>
              <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${review.length > 0 ? "bg-red-600 text-white" : "bg-gray-200 text-gray-500"}`}>
                {review.length}
              </span>
              <span className="text-[10px] text-gray-400 uppercase tracking-wider">Ready to close</span>
            </div>
            {openReviewAccordion ? <ChevronUp className="h-4 w-4 text-gray-500" /> : <ChevronDown className="h-4 w-4 text-gray-500" />}
          </button>
          {openReviewAccordion && (
            <div className="p-2 space-y-2 max-h-[400px] overflow-y-auto">
              {loading && <p className="text-xs text-gray-400 text-center py-4">Loading...</p>}
              {!loading && review.length === 0 && (
                <p className="text-[11px] text-gray-400 text-center py-4">Nothing waiting for review</p>
              )}
              {review.map(r => (
                <HoldCallCard key={r.id} record={r} kind="review" onChanged={fetchAll} isAdmin={isAdmin} />
              ))}
            </div>
          )}
        </div>

        {/* Holding accordion — 48h wait */}
        <div
          className="rounded-lg border-2 border-[#B0803E]/30 bg-[#B0803E]/5"
          data-testid="hold-holding-folder"
        >
          <button
            onClick={() => setOpenHoldingAccordion(v => !v)}
            className="w-full px-3 py-2.5 flex items-center justify-between hover:bg-[#B0803E]/10 transition-colors"
          >
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-[#B0803E]" />
              <span className="text-sm font-bold text-[#1F2937]">Holding (48h wait)</span>
              <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-[#B0803E]/20 text-[#6F4E1D]">{holding.length}</span>
              <span className="text-[10px] text-gray-400 uppercase tracking-wider">Waiting for agency</span>
            </div>
            {openHoldingAccordion ? <ChevronUp className="h-4 w-4 text-gray-500" /> : <ChevronDown className="h-4 w-4 text-gray-500" />}
          </button>
          {openHoldingAccordion && (
            <div className="p-2 space-y-2 max-h-[400px] overflow-y-auto">
              {loading && <p className="text-xs text-gray-400 text-center py-4">Loading...</p>}
              {!loading && holding.length === 0 && (
                <p className="text-[11px] text-gray-400 text-center py-4">
                  <Building2 className="h-4 w-4 inline mr-1 text-gray-300" />
                  No orders currently on hold
                </p>
              )}
              {holding.map(r => (
                <HoldCallCard key={r.id} record={r} kind="holding" onChanged={fetchAll} isAdmin={isAdmin} />
              ))}
            </div>
          )}
        </div>
      </div>

      {showCreate && <HoldCallModal onClose={() => setShowCreate(false)} onSaved={fetchAll} />}
    </div>
  );
}
